/* Greatly Social – account handler utilities (v2.3 LinkedIn Fix)
    -------------------------------------------------------------------
    • FIX: Correctly calls the /pending endpoint for new LinkedIn connections.
    • Refactors LinkedIn OAuth handling into a single, clean useEffect.
*/

import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountsContext } from '../../../../context/AccountsContext';
import { SITE_URL } from '../../../../constants';

/* ─────────── helpers ──────────── */
const PLATFORM_TEMPLATE = [
  { platform: 'Facebook',  connectedPages: [] },
  { platform: 'Instagram', connectedPages: [] },
  { platform: 'X',         connectedPages: [] },
  { platform: 'LinkedIn',  connectedPages: [] },
  { platform: 'Threads',   connectedPages: [] },
  { platform: 'TikTok',    connectedPages: [] },
  { platform: 'Pinterest', connectedPages: [] },
  { platform: 'YouTube',   connectedPages: [] }
];

function groupByPlatform (flat = []) {
  const grouped = PLATFORM_TEMPLATE.map(p => ({ ...p, connectedPages: [] }));
  flat.forEach(acc => {
    const idx = grouped.findIndex(
      g => g.platform.toLowerCase() === (acc.platform || '').toLowerCase()
    );
    if (idx !== -1) grouped[idx].connectedPages.push(acc);
  });
  return grouped;
}

/* MAIN HOOK ───────────────────────────────────────────────────────── */
export function useAccountHandlers () {
  const navigate = useNavigate();
  const {
    accounts: ctxAccounts,
    loading : ctxLoading,
    refresh : refreshAccounts
  } = useContext(AccountsContext);

  const [accounts, setAccounts] = useState(groupByPlatform(ctxAccounts));
  useEffect(() => {
    if (!ctxLoading) setAccounts(groupByPlatform(ctxAccounts));
  }, [ctxAccounts, ctxLoading]);

  /* ---------- quota / limits ---------- */
  const [acctRemaining, setAcctRemaining] = useState(null);
  const  isAtLimit = acctRemaining !== null && acctRemaining === 0;

  const [showModal,          setShowModal]          = useState(false);
  const [showUpgradeModal,   setShowUpgradeModal]   = useState(false);

  /* ---------- per-platform state ---------- */
  /* Facebook */
  const [showFbPagesModal, setShowFbPagesModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal]   = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [fbPages, setFbPages]                   = useState([]);
  const [selectedFbPageIds, setSelectedFbPageIds] = useState([]);

  /* Instagram */
  const [showIgModal, setShowIgModal]                   = useState(false);
  const [showIgDeleteModal, setShowIgDeleteModal]       = useState(false);
  const [showIgDeleteConfirmModal, setShowIgDeleteConfirmModal] = useState(false);
  const [igAccounts, setIgAccounts]                     = useState([]);
  const [selectedIgIds, setSelectedIgIds]               = useState([]);

  /* Threads */
  const [showThreadsModal, setShowThreadsModal]                 = useState(false);
  const [showThreadsDeleteModal, setShowThreadsDeleteModal]     = useState(false);
  const [showThreadsDeleteConfirmModal, setShowThreadsDeleteConfirmModal] = useState(false);
  const [threadsAccounts, setThreadsAccounts]                   = useState([]);
  const [selectedThreadsIds, setSelectedThreadsIds]             = useState([]);

  /* X / Twitter */
  const [showXModal, setShowXModal]                   = useState(false);
  const [showXDeleteModal, setShowXDeleteModal]       = useState(false);
  const [showXDeleteConfirmModal, setShowXDeleteConfirmModal] = useState(false);
  const [xAccounts, setXAccounts]                     = useState([]);
  const [selectedXIds, setSelectedXIds]               = useState([]);

  /* LinkedIn */
  const [showLnUnifiedModal, setShowLnUnifiedModal]             = useState(false);
  const [showLnDeleteModal, setShowLnDeleteModal]               = useState(false);
  const [showLnDeleteConfirmModal, setShowLnDeleteConfirmModal] = useState(false);
  const [lnAccounts, setLnAccounts]                             = useState([]);
  const [lnPages, setLnPages]                                   = useState([]);
  const [selectedLnCombined, setSelectedLnCombined]             = useState([]);

  /* TikTok */
  const [showTikTokModal, setShowTikTokModal]                 = useState(false);
  const [showTikTokDeleteModal, setShowTikTokDeleteModal]     = useState(false);
  const [showTikTokDeleteConfirmModal, setShowTikTokDeleteConfirmModal] = useState(false);
  const [tikTokAccounts, setTikTokAccounts]                   = useState([]);
  const [selectedTikTokIds, setSelectedTikTokIds]             = useState([]);

  /* Pinterest */
  const [showPinterestModal, setShowPinterestModal]                 = useState(false);
  const [showPinterestDeleteModal, setShowPinterestDeleteModal]     = useState(false);
  const [showPinterestDeleteConfirmModal, setShowPinterestDeleteConfirmModal] = useState(false);
  const [pinterestAccounts, setPinterestAccounts]                   = useState([]);
  const [selectedPinterestIds, setSelectedPinterestIds]             = useState([]);

  /* YouTube */
  const [showYouTubeModal, setShowYouTubeModal]                 = useState(false);
  const [showYouTubeDeleteModal, setShowYouTubeDeleteModal]     = useState(false);
  const [showYouTubeDeleteConfirmModal, setShowYouTubeDeleteConfirmModal] = useState(false);
  const [youtubeAccounts, setYouTubeAccounts]                   = useState([]);
  const [selectedYouTubeIds, setSelectedYouTubeIds]             = useState([]);

  /* ---------- EFFECTS ---------- */

  /* 1) fetch quota once */
  useEffect(() => {
    fetch('/api/billing/usage', { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (d?.accounts) {
          const { used = 0, limit = 0 } = d.accounts;
          setAcctRemaining(Math.max(limit - used, 0));
        }
      })
      .catch(e => console.error('[quota] fetch failed:', e));
  }, []);

  /* 2) handle OAuth callbacks for every platform */
  useEffect(() => {
    if (window.location.hash === '#_=_') {
      window.history.replaceState('', document.title,
        window.location.pathname + window.location.search);
    }
    const params = new URLSearchParams(window.location.search);

    const cleanupUrl = (paramName) => {
        params.delete(paramName);
        navigate({ search: params.toString() }, { replace: true });
    };

    /* ---------- FACEBOOK ---------- */
    if (params.get('fb_connected') === '1') {
      fetch('/api/auth/facebook/pages', { credentials: 'include' })
        .then(r => r.json())
        .then(arr => {
          if (Array.isArray(arr)) {
            setFbPages(arr);
            setShowFbPagesModal(true);
          }
        })
        .catch(e => console.error('Fetch FB pages error:', e))
        .finally(() => cleanupUrl('fb_connected'));
    }

    /* ---------- INSTAGRAM ---------- */
    if (params.get('ig_pending') === '1') {
      fetch('/api/auth/instagram/pending', { credentials: 'include' })
        .then(r => r.json())
        .then(arr => {
          if (Array.isArray(arr)) {
            setIgAccounts(arr);
            setShowIgModal(true);
          }
        })
        .catch(e => console.error('Failed to load pending IG:', e))
        .finally(() => cleanupUrl('ig_pending'));
    }

    /* ---------- THREADS ---------- */
    if (params.get('threads_connected') === '1') {
      fetch('/api/auth/threads/pending', { credentials: 'include' })
        .then(r => r.json())
        .then(arr => {
          if (Array.isArray(arr)) {
            setThreadsAccounts(arr);
            setShowThreadsModal(true);
          }
        })
        .catch(e => console.error('Fetch Threads accounts error:', e))
        .finally(() => cleanupUrl('threads_connected'));
    }

    /* ---------- X / TWITTER ---------- */
    if (params.get('x_connected') === '1') {
      fetch('/api/auth/twitter/pending', { credentials: 'include' })
        .then(r => r.json())
        .then(arr => {
          if (Array.isArray(arr) && arr.length) {
            setXAccounts(arr);
            setShowXModal(true);
          }
        })
        .catch(e => console.error('Fetch X pending error:', e))
        .finally(() => cleanupUrl('x_connected'));
    }

    // --- THIS IS THE FIX ---
    // It now correctly checks for `ln_connected=1` and calls the `/pending` endpoint.
    if (params.get('ln_connected') === '1') {
      fetch('/api/auth/linkedin/pending', { credentials: 'include' })
        .then(r => r.json())
        .then(data => {
          if (data && data.profiles && data.organizations) {
            setLnAccounts(data.profiles);
            setLnPages(data.organizations);
            setShowLnUnifiedModal(true);
          }
        })
        .catch(e => console.error('Error fetching pending LN account:', e))
        .finally(() => cleanupUrl('ln_connected'));
    }

    /* ---------- TIKTOK ---------- */
    if (params.get('tiktok_pending') === '1') {
      fetch('/api/auth/tiktok/pending', { credentials: 'include' })
        .then(r => r.json())
        .then(d => {
          if (d?.tiktokId) {
            setTikTokAccounts([{ ...d }]);
            setShowTikTokModal(true);
          }
        })
        .catch(e => console.error('Error fetching pending TikTok account:', e))
        .finally(() => cleanupUrl('tiktok_pending'));
    }

    /* ---------- PINTEREST ---------- */
    if (params.get('pinterest_pending') === '1') {
      fetch('/api/auth/pinterest/pending', { credentials: 'include' })
        .then(r => r.json())
        .then(arr => {
          if (Array.isArray(arr) && arr.length) {
            setPinterestAccounts(arr);
            setShowPinterestModal(true);
          }
        })
        .catch(e => console.error('Error fetching pending Pinterest account:', e))
        .finally(() => cleanupUrl('pinterest_pending'));
    }

    /* ---------- YOUTUBE ---------- */
    if (params.get('youtube_pending') === '1') {
      fetch('/api/auth/youtube/pending', { credentials: 'include' })
        .then(r => r.json())
        .then(arr => {
          if (Array.isArray(arr) && arr.length) {
            setYouTubeAccounts(arr);
            setShowYouTubeModal(true);
          }
        })
        .catch(e => console.error('Fetch YouTube pending error:', e))
        .finally(() => cleanupUrl('youtube_pending'));
    }
  }, []); // This effect should run only once on page load

  /* ============ GENERIC OAUTH LAUNCHER ============ */
  function handleConnectPlatform (platform) {
    const base = '/api/auth/';
    switch (platform.toLowerCase()) {
      case 'facebook':  window.location.href = `${base}facebook`;  break;
      case 'instagram': window.location.href = `${base}instagram`; break;
      case 'threads':   window.location.href = `${base}threads`;   break;
      case 'x':         window.location.href = `${base}twitter`;   break;
      case 'linkedin':  window.location.href = `${base}linkedin`;  break;
      case 'tiktok':    window.location.href = `${base}tiktok`;    break;
      case 'pinterest': window.location.href = `${base}pinterest`; break;
      case 'youtube':   window.location.href = `${base}youtube`;   break;
      default: alert(`OAuth flow not implemented yet for ${platform}.`);
    }
    setShowModal(false);
  }

  /* ... (rest of the handler functions remain the same) ... */
  async function openFbDeleteModal () {
    try {
      const resp  = await fetch('/api/auth/facebook/pages/connected', { credentials: 'include' });
      const rows  = await resp.json();
      const normalised = Array.isArray(rows) ? rows.map(r => ({
          id        : r.fb_page_id,
          name      : r.fb_page_name,
          pictureUrl : r.fb_page_picture_url,
        })) : [];
      setFbPages(normalised);
    } catch (err) {
      console.error('Failed to fetch connected FB pages:', err);
      setFbPages([]);
    }
    setSelectedFbPageIds([]);
    setShowDeleteModal(true);
  }
  function handleFbCheckboxChange (pageId) {
    setSelectedFbPageIds(prev => Array.isArray(pageId) ? pageId : prev.includes(pageId) ? prev.filter(id => id !== pageId) : [...prev, pageId]);
  }
  function handleConnectSelectedPages () {
    if (!selectedFbPageIds.length) { alert('Select at least one page.'); return; }
    const payload = fbPages
      .filter(p => selectedFbPageIds.includes(String(p.id ?? p.fb_page_id ?? '')))
      .map(p => ({
        id: String(p.id ?? p.fb_page_id ?? ''),
        name: p.name,
        pictureUrl : p.picture?.data?.url || p.pictureUrl || '',
        accessToken: p.access_token || null,
      }));
    fetch('/api/auth/facebook/pages/connect', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedPages: payload }) })
      .then(() => {
        setShowFbPagesModal(false);
        setSelectedFbPageIds([]);
        refreshAccounts(); // Use context refresh instead of hard reload
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error connecting FB pages:', e));
  }
  function handleDeleteSelectedPages () {
    if (!selectedFbPageIds.length) { alert('Select at least one page.'); return; }
    setShowDeleteModal(false);
    setShowDeleteConfirmModal(true);
  }
  function handleDeleteSelectedPagesConfirm () {
    fetch('/api/auth/facebook/pages/delete', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedPages: selectedFbPageIds }) })
      .then(res => {
        // fetch() only rejects on network errors, so a 401/500 would otherwise
        // look like success here — the page silently reappears on refresh and
        // the user reads it as "won't delete". Surface a real error instead.
        if (!res.ok) throw new Error(`Delete failed (HTTP ${res.status})`);
        setShowDeleteConfirmModal(false);
        setSelectedFbPageIds([]);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => {
        console.error('Error deleting FB pages:', e);
        alert('Could not remove the selected Facebook page(s). Please refresh and try again — if it keeps happening, reconnect your Facebook account.');
      });
  }
  function handleIgCheckboxChange (id) { setSelectedIgIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); }
  function handleConnectSelectedIg () {
    if (!selectedIgIds.length) { alert('Select at least one IG account.'); return; }
    const payload = igAccounts.filter(a => selectedIgIds.includes(a.igId)).map(a => ({ igId: a.igId, igUsername: a.igUsername, igProfilePic: a.igProfilePic || '', accessToken : a.accessToken || '' }));
    fetch('/api/auth/instagram/accounts/connect', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedIGAccounts: payload }) })
      .then(() => {
        setShowIgModal(false);
        setSelectedIgIds([]);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error connecting IG accounts:', e));
  }
  function handleDeleteIgAccounts () { if (!selectedIgIds.length) { alert('Select at least one IG account.'); return; } setShowIgDeleteModal(false); setShowIgDeleteConfirmModal(true); }
  function handleDeleteIgConfirm () {
    const payload = selectedIgIds.map(id => ({ igId: id }));
    fetch('/api/auth/instagram/accounts/delete', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedIGAccounts: payload }) })
      .then(() => {
        setShowIgDeleteConfirmModal(false);
        setSelectedIgIds([]);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error deleting IG accounts:', e));
  }
  function handleThreadsCheckboxChange (id) { setSelectedThreadsIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); }
  function handleConnectSelectedThreads () {
    if (!selectedThreadsIds.length) { alert('Select at least one Threads account.'); return; }
    const payload = threadsAccounts.filter(a => selectedThreadsIds.includes(a.threadsId)).map(a => ({ threadsId: a.threadsId, username: a.username, threadsProfilePic : a.threadsProfilePic || '', url: a.url || '', accessToken: a.accessToken || '' }));
    fetch('/api/auth/threads/accounts/connect', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedThreadsAccounts: payload }) })
      .then(() => {
        setShowThreadsModal(false);
        setSelectedThreadsIds([]);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error connecting Threads:', e));
  }
  function handleDeleteThreadsAccounts () { if (!selectedThreadsIds.length) { alert('Select at least one Threads account.'); return; } setShowThreadsDeleteModal(false); setShowThreadsDeleteConfirmModal(true); }
  function handleDeleteThreadsConfirm () {
    const payload = selectedThreadsIds.map(id => ({ threadsId: id }));
    fetch('/api/auth/threads/accounts/delete', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedThreadsAccounts: payload }) })
      .then(() => {
        setShowThreadsDeleteConfirmModal(false);
        setSelectedThreadsIds([]);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error deleting Threads accounts:', e));
  }
  function handleXCheckboxChange (id) { setSelectedXIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); }
  function handleConnectSelectedX () {
    if (!selectedXIds.length) { alert('Select at least one X account.'); return; }
    const payload = xAccounts.filter(a => selectedXIds.includes(a.twitterId)).map(a => ({ twitterId: a.twitterId, displayName : a.displayName, profileImage: a.profileImage || '' }));
    fetch('/api/auth/twitter/accounts/connect', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedTwitterAccounts: payload }) })
      .then(() => {
        setShowXModal(false);
        setSelectedXIds([]);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error connecting X accounts:', e));
  }
  function handleDeleteXAccounts () { if (!selectedXIds.length) { alert('Select at least one X account.'); return; } setShowXDeleteModal(false); setShowXDeleteConfirmModal(true); }
  function handleDeleteXConfirm () {
    const payload = selectedXIds.map(id => ({ twitterId: id }));
    fetch('/api/auth/twitter/accounts/delete', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedTwitterAccounts: payload }) })
      .then(() => {
        setShowXDeleteConfirmModal(false);
        setSelectedXIds([]);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error deleting X accounts:', e));
  }
  function handleLnUnifiedCheckbox (key) { setSelectedLnCombined(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]); }
  function handleConnectSelectedLnUnified () {
    if (!selectedLnCombined.length) { alert('Select at least one LinkedIn item.'); return; }
    const combined = [
      ...lnAccounts.map(a => ({ type: 'personal', lnId: a.lnId, displayName: a.displayName, profileImage: a.profileImage || '' })),
      ...lnPages.map(p => ({ type: 'page', id: p.id, name: p.name, logoUrl: p.logoUrl || '' }))
    ];
    const payload = combined.filter(item => selectedLnCombined.includes(item.type === 'page' ? item.id : item.lnId))
      .map(item => item.type === 'page' ? { lnId: item.id, displayName: item.name, profileImage: item.logoUrl } : { lnId: item.lnId, displayName: item.displayName, profileImage: item.profileImage });
    fetch('/api/auth/linkedin/accounts/connect', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedLinkedInAccounts: payload }) })
      .then(() => {
        setShowLnUnifiedModal(false);
        setSelectedLnCombined([]);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error connecting LN accounts:', e));
  }
  function handleCloseLnUnifiedModal () { setSelectedLnCombined([]); setShowLnUnifiedModal(false); }
  function handleDeleteLnAccounts () { setShowLnDeleteModal(false); setShowLnDeleteConfirmModal(true); }
  function handleDeleteLnConfirm () {
    const lnAcc = accounts.find(a => a.platform.toLowerCase() === 'linkedin');
    const personalOnly = lnAcc.connectedPages.filter(p => p.lnId);
    const payload = personalOnly.map(p => ({ lnId: p.lnId }));
    fetch('/api/auth/linkedin/accounts/delete', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedLinkedInAccounts: payload }) })
      .then(() => {
        setShowLnDeleteConfirmModal(false);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error deleting LN accounts:', e));
  }
  function handleTikTokCheckboxChange (id) { setSelectedTikTokIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); }
  function handleConnectSelectedTikTok () {
    if (!selectedTikTokIds.length) { alert('Select at least one TikTok account.'); return; }
    const payload = tikTokAccounts.filter(a => selectedTikTokIds.includes(a.tiktokId)).map(a => ({ ...a }));
    fetch('/api/auth/tiktok/accounts/connect', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedTikTokAccounts: payload }) })
      .then(() => {
        setShowTikTokModal(false);
        setSelectedTikTokIds([]);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error connecting TikTok accounts:', e));
  }
  function handleDeleteTikTokAccounts () { if (!selectedTikTokIds.length) { alert('Select at least one TikTok account.'); return; } setShowTikTokDeleteModal(false); setShowTikTokDeleteConfirmModal(true); }
  function handleDeleteTikTokConfirm () {
    const payload = selectedTikTokIds.map(id => ({ tiktokId: id }));
    fetch('/api/auth/tiktok/accounts/delete', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedTikTokAccounts: payload }) })
      .then(() => {
        setShowTikTokDeleteConfirmModal(false);
        setSelectedTikTokIds([]);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error deleting TikTok accounts:', e));
  }
  function handlePinterestCheckboxChange (id) { setSelectedPinterestIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); }
  function handleConnectSelectedPinterest () {
    if (!selectedPinterestIds.length) { alert('Select at least one Pinterest account.'); return; }
    const payload = pinterestAccounts.filter(a => selectedPinterestIds.includes(a.pinterestId)).map(a => ({ ...a }));
    fetch('/api/auth/pinterest/accounts/connect', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedPinterestAccounts: payload }) })
      .then(() => {
        setShowPinterestModal(false);
        setSelectedPinterestIds([]);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error connecting Pinterest accounts:', e));
  }
  function handleDeletePinterestAccounts () { if (!selectedPinterestIds.length) { alert('Select at least one Pinterest account.'); return; } setShowPinterestDeleteModal(false); setShowPinterestDeleteConfirmModal(true); }
  function handleDeletePinterestConfirm () {
    const payload = selectedPinterestIds.map(id => ({ pinterestId: id }));
    fetch('/api/auth/pinterest/accounts/delete', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedPinterestAccounts: payload }) })
      .then(() => {
        setShowPinterestDeleteConfirmModal(false);
        setSelectedPinterestIds([]);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error deleting Pinterest accounts:', e));
  }
  function handleYouTubeCheckboxChange (id) { setSelectedYouTubeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); }
  function handleConnectSelectedYouTube () {
    if (!selectedYouTubeIds.length) { alert('Select at least one YouTube channel.'); return; }
    const payload = youtubeAccounts.filter(a => selectedYouTubeIds.includes(a.youtubeId)).map(a => ({ ...a }));
    fetch('/api/auth/youtube/accounts/connect', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedYouTubeAccounts: payload }) })
      .then(() => {
        setShowYouTubeModal(false);
        setSelectedYouTubeIds([]);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error connecting YouTube channels:', e));
  }
  function handleDeleteYouTubeAccounts () { if (!selectedYouTubeIds.length) { alert('Select at least one YouTube channel.'); return; } setShowYouTubeDeleteModal(false); setShowYouTubeDeleteConfirmModal(true); }
  function handleDeleteYouTubeConfirm () {
    const payload = selectedYouTubeIds.map(id => ({ youtubeId: id }));
    fetch('/api/auth/youtube/accounts/delete', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selectedYouTubeAccounts: payload }) })
      .then(() => {
        setShowYouTubeDeleteConfirmModal(false);
        setSelectedYouTubeIds([]);
        refreshAccounts();
        navigate('/dashboard/accounts', { replace: true });
      })
      .catch(e => console.error('Error deleting YouTube channels:', e));
  }

  return {
    state: {
      accounts, loading: ctxLoading, acctRemaining, isAtLimit, showModal, showUpgradeModal,
      showFbPagesModal, showDeleteModal, showDeleteConfirmModal, fbPages, selectedFbPageIds,
      showIgModal, showIgDeleteModal, showIgDeleteConfirmModal, igAccounts, selectedIgIds,
      showThreadsModal, showThreadsDeleteModal, showThreadsDeleteConfirmModal, threadsAccounts, selectedThreadsIds,
      showXModal, showXDeleteModal, showXDeleteConfirmModal, xAccounts, selectedXIds,
      showLnUnifiedModal, showLnDeleteModal, showLnDeleteConfirmModal, lnAccounts, lnPages, selectedLnCombined,
      showTikTokModal, showTikTokDeleteModal, showTikTokDeleteConfirmModal, tikTokAccounts, selectedTikTokIds,
      showPinterestModal, showPinterestDeleteModal, showPinterestDeleteConfirmModal, pinterestAccounts, selectedPinterestIds,
      showYouTubeModal, showYouTubeDeleteModal, showYouTubeDeleteConfirmModal, youtubeAccounts, selectedYouTubeIds
    },
    setShowModal, setShowUpgradeModal,
    setShowFbPagesModal, setShowDeleteModal, setShowDeleteConfirmModal, setSelectedFbPageIds,
    setShowIgModal, setShowIgDeleteModal, setShowIgDeleteConfirmModal, setSelectedIgIds,
    setShowThreadsModal, setShowThreadsDeleteModal, setShowThreadsDeleteConfirmModal, setSelectedThreadsIds,
    setShowXModal, setShowXDeleteModal, setShowXDeleteConfirmModal, setSelectedXIds,
    setShowLnUnifiedModal, setShowLnDeleteModal, setShowLnDeleteConfirmModal, setSelectedLnCombined,
    setShowTikTokModal, setShowTikTokDeleteModal, setShowTikTokDeleteConfirmModal, setSelectedTikTokIds,
    setShowPinterestModal, setShowPinterestDeleteModal, setShowPinterestDeleteConfirmModal, setSelectedPinterestIds,
    setShowYouTubeModal, setShowYouTubeDeleteModal, setShowYouTubeDeleteConfirmModal, setSelectedYouTubeIds,
    handleConnectPlatform,
    handleFbCheckboxChange, handleConnectSelectedPages, handleDeleteSelectedPages, handleDeleteSelectedPagesConfirm, openFbDeleteModal,
    handleIgCheckboxChange, handleConnectSelectedIg, handleDeleteIgAccounts, handleDeleteIgConfirm,
    handleThreadsCheckboxChange, handleConnectSelectedThreads, handleDeleteThreadsAccounts, handleDeleteThreadsConfirm,
    handleXCheckboxChange, handleConnectSelectedX, handleDeleteXAccounts, handleDeleteXConfirm,
    handleLnUnifiedCheckbox, handleConnectSelectedLnUnified, handleCloseLnUnifiedModal, handleDeleteLnAccounts, handleDeleteLnConfirm,
    handleTikTokCheckboxChange, handleConnectSelectedTikTok, handleDeleteTikTokAccounts, handleDeleteTikTokConfirm,
    handlePinterestCheckboxChange, handleConnectSelectedPinterest, handleDeletePinterestAccounts, handleDeletePinterestConfirm,
    handleYouTubeCheckboxChange, handleConnectSelectedYouTube, handleDeleteYouTubeAccounts, handleDeleteYouTubeConfirm
  };
}
